import React, { useCallback, useEffect, useRef, useState } from 'react';
import { deleteSavedHealthReport, getHealthHistory, getSavedHealthReport, runHealthCheck } from '../../services/api';
import { moneyShort } from './format';
import { downloadHealthReport, downloadInsightsReport } from './reportDownload';

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export default function ClientsOverview({ onOpen }) {
  const [clients, setClients] = useState(null);
  const [busy, setBusy] = useState(null); // tenantId being run
  const [progress, setProgress] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const cancelled = useRef(false);

  const refresh = useCallback(() => getHealthHistory().then(setClients).catch(err => setError(err.response?.data?.error || 'Could not load clients')), []);
  useEffect(() => { refresh(); return () => { cancelled.current = true; }; }, [refresh]);

  async function runOne(tenantId) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await runHealthCheck(tenantId);
        return null;
      } catch (err) {
        const status = err.response?.status;
        if (status === 429 && attempt === 0) { await sleep(60000); continue; }
        return err.response?.data?.error || err.message;
      }
    }
    return 'Rate limited';
  }

  async function run(ids) {
    setError(null);
    cancelled.current = false;
    const failures = [];
    for (let i = 0; i < ids.length && !cancelled.current; i++) {
      setBusy(ids[i]);
      setProgress(ids.length > 1 ? `${i + 1} of ${ids.length}` : null);
      const e = await runOne(ids[i]);
      if (e) failures.push(`${clients.find(c => c.tenantId === ids[i])?.name}: ${e}`);
      await refresh();
    }
    setBusy(null); setProgress(null);
    if (failures.length) setError(failures.join(' · '));
  }

  async function downloadSaved(reportId, clientName, type) {
    setError(null);
    setDownloading(`${reportId}-${type}`);
    try {
      const saved = await getSavedHealthReport(reportId);
      if (type === 'health') downloadHealthReport(saved.report, saved.tenantName || clientName, saved.createdAt);
      else downloadInsightsReport(saved.report, saved.tenantName || clientName, saved.createdAt);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not download the saved report.');
    } finally {
      setDownloading(null);
    }
  }

  async function deleteSaved(reportId, clientName) {
    if (!window.confirm(`Delete the saved detailed report for ${clientName}? The score history will be kept.`)) return;
    setError(null);
    setDeleting(reportId);
    try {
      await deleteSavedHealthReport(reportId);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not delete the saved report.');
    } finally {
      setDeleting(null);
    }
  }

  if (!clients) return error ? <div className="hc-note">{error}</div> : <div className="running-state"><div className="spinner" /></div>;

  return (
    <>
      <div className="hc-actions">
        <button className="btn-secondary" disabled={!!busy} onClick={() => run(clients.map(c => c.tenantId))}>
          {busy && progress ? `Running ${progress}…` : 'Run all clients'}
        </button>
        {busy && progress && <button className="btn-ghost" onClick={() => { cancelled.current = true; }}>Stop after this one</button>}
      </div>
      {error && <div className="hc-note">{error}</div>}
      <div className="cl-table-wrap">
        <table className="cl-table">
          <thead>
            <tr><th>Client</th><th>Score</th><th>Action / review</th><th>Unreconciled</th><th>Cash cover</th><th>12m profit</th><th>Last run</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {clients.map(c => {
              const r = c.lastRun;
              const change = r && r.previousScore !== null && r.score !== null ? r.score - r.previousScore : null;
              return (
                <tr key={c.tenantId}>
                  <td><button className="cl-link" onClick={() => onOpen(c.tenantId)}>{c.name}</button></td>
                  <td className={r ? `hc-score-${r.rating}` : ''}>
                    {r && r.score !== null ? `${r.score}%` : '—'}
                    {change ? <span className="cl-change">{change > 0 ? ` ▲${change}` : ` ▼${Math.abs(change)}`}</span> : null}
                  </td>
                  <td>{r ? `${r.failCount} / ${r.warnCount}` : '—'}</td>
                  <td>{r?.summary?.unreconciled ?? '—'}</td>
                  <td>{r?.summary?.cashRatio != null ? `${r.summary.cashRatio}×` : '—'}</td>
                  <td>{r?.summary?.netProfit12m != null ? moneyShort(r.summary.netProfit12m) : '—'}</td>
                  <td>{r ? new Date(r.at).toLocaleDateString('en-GB') : 'Never'}</td>
                  <td>
                    <div className="cl-actions">
                      <button className="btn-ghost" disabled={!!busy || !!downloading || !!deleting} onClick={() => run([c.tenantId])}>{busy === c.tenantId ? 'Running…' : 'Run'}</button>
                      {r?.savedReportId && <>
                        <button className="btn-ghost" disabled={!!busy || !!downloading || !!deleting} onClick={() => downloadSaved(r.savedReportId, c.name, 'health')}>
                          {downloading === `${r.savedReportId}-health` ? 'Preparing…' : 'Health report'}
                        </button>
                        <button className="btn-ghost" disabled={!!busy || !!downloading || !!deleting} onClick={() => downloadSaved(r.savedReportId, c.name, 'insights')}>
                          {downloading === `${r.savedReportId}-insights` ? 'Preparing…' : 'Insights report'}
                        </button>
                        <button className="btn-ghost danger" disabled={!!busy || !!downloading || !!deleting} onClick={() => deleteSaved(r.savedReportId, c.name)}>
                          {deleting === r.savedReportId ? 'Deleting…' : 'Delete saved'}
                        </button>
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
